import { useState, useEffect } from 'react';
import {
  IconRefresh,
  ButtonSize,
  Popup,
  formatDate,
  ButtonSecondary,
  ButtonPseudo,
  IconEdit,
  ButtonPrimary,
  IconDots,
  ClickOutsideDetector,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  CustomerScoreUpdateStatus,
  AmlAction,
  CustomerStatus,
  ClientScoreTypeEnum,
} from '../../../hoc/customers/types';
import { TotalRiskScoringTypes } from './types';

import cn from './aml.module.css';

const TotalRiskScoring = ({
  logs,
  onUpdate,
  updateIsSuccess,
  updateInProgress,
  aml,
  onCollapse,
  blockCollapsed,
  amlUpdateStatus,
  calculateAllowed,
  status,
  isLoading,
  users,
  manualScoreAllowed,
  changeLogsAvailable,
  setManualScoreSidepanelVisible,
  setRiskScoreChangeLogsSidepanelVisible,
}: TotalRiskScoringTypes): React.JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [riskScoreMenuVisible, setRiskScoreMenuVisible] = useState(false);

  const { riskScoreRanges } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  let totalRiskClassName: string;

  const riskLevel = riskScoreRanges?.find(
    (item) =>
      (aml?.totalRiskScore as number) >= item.rangeStart &&
      (aml?.totalRiskScore as number) <= item.rangeEnd,
  );

  if (riskLevel?.action === AmlAction.NONE) {
    totalRiskClassName = cn.totalRiskScoringGood;
  } else if (riskLevel?.action === AmlAction.OPS_REVIEW) {
    totalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    totalRiskClassName = cn.totalRiskScoringLow;
  }

  useEffect(() => {
    if (updateIsSuccess) {
      setPopupVisible(false);
    }
  }, [updateIsSuccess]);

  useEffect(() => {
    if (aml?.clientScoreType === ClientScoreTypeEnum.MANUAL && blockCollapsed) {
      onCollapse();

      setExpanded(false);
    }
  }, [aml, blockCollapsed]);

  return (
    <div className={cn.totalRiskScoring}>
      <div
        className={`${cn.totalRiskScoringTop} ${
          !blockCollapsed ? cn.totalRiskScoringTopRounded : ''
        }`}
      >
        <button
          type="button"
          className={cn.collapseBlockButton}
          onClick={() => {
            onCollapse();
            if (!expanded) {
              setExpanded(true);
            } else if (expanded && blockCollapsed) {
              setExpanded(false);
            }
          }}
          disabled={Boolean(
            aml?.clientScoreType === ClientScoreTypeEnum.MANUAL,
          )}
          aria-label="Collapse button"
        />
        {isLoading && !aml && (
          <div className={cn.riskScoreTotalText}>
            Customer Risk Score is Loading...
          </div>
        )}
        {!isLoading &&
          !aml &&
          amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED &&
          amlUpdateStatus !== CustomerScoreUpdateStatus.FAIL && (
            <div className={cn.riskScoreTotalText}>
              Customer Risk Score is calculating...
            </div>
          )}
        <div className={cn.totalRiskScoringTopLeft}>
          {amlUpdateStatus === CustomerScoreUpdateStatus.FAIL && !aml && (
            <div className={cn.statusFailed}>
              Update Customer Risk Score Failed. Try again
            </div>
          )}
          {amlUpdateStatus === CustomerScoreUpdateStatus.QUEUED && !aml && (
            <div className={cn.riskScoreTotalText}>
              The customer&apos;s risk score is calculating
            </div>
          )}
          {aml && (
            <>
              <div
                className={`${cn.totalRiskScoringValue} ${totalRiskClassName}`}
              >
                {aml?.totalRiskScore}
              </div>
              <div
                className={`${cn.totalRiskScoringLevel} ${totalRiskClassName}`}
              >{`${riskLevel?.name.toLowerCase() || 'High'} Risk`}</div>
              <div className={cn.totalRiskScoringType}>
                {aml?.clientScoreType === ClientScoreTypeEnum.AUTO ? (
                  <span>
                    <IconRefresh width={14} height={14} /> auto
                  </span>
                ) : (
                  <span>
                    <IconEdit width={14} height={14} /> manually
                  </span>
                )}
              </div>
              <div className={cn.totalRiskScoringDescription}>
                <div className={cn.totalRiskScoringCalculatedDate}>
                  {aml?.updatedAt || aml?.createdAt ? (
                    <span>
                      {formatDate(aml?.updatedAt || aml?.createdAt, true)}
                    </span>
                  ) : (
                    ''
                  )}
                </div>
                {aml?.employeeId && (
                  <>
                    <div className={cn.amlCircle} />
                    <div className={cn.riskScoringEmployee}>
                      {
                        users?.find((item) => item.value === aml?.employeeId)
                          ?.name
                      }
                    </div>
                  </>
                )}
              </div>

              {amlUpdateStatus === CustomerScoreUpdateStatus.FAIL && (
                <div className={cn.updateStatusFailed}>
                  Update Customer Application Risk Score Failed. Try again
                </div>
              )}
            </>
          )}
        </div>
        <div className={cn.totalRiskScoringTopRight}>
          <div className={`${cn.totalRiskScoringButtonWrapper} ${cn.hidden}`}>
            <ButtonPseudo
              size={ButtonSize.SMALL}
              text="Change Log"
              onClick={() => setExpanded(!expanded)}
            />
          </div>
          {manualScoreAllowed && status !== CustomerStatus.CLOSED && (
            <div className={cn.totalRiskScoringButtonWrapper}>
              <ButtonSecondary
                size={ButtonSize.SMALL}
                text="Set manually"
                className={`button-white ${cn.setRiskScoreButton}`}
                icon={<IconEdit />}
                onClick={() => setManualScoreSidepanelVisible(true)}
              />
            </div>
          )}
          {calculateAllowed && (
            <div className={cn.totalRiskScoringButtonWrapper}>
              {status !== CustomerStatus.CLOSED && (
                <ButtonPrimary
                  icon={
                    amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED ? (
                      <IconRefresh />
                    ) : undefined
                  }
                  size={ButtonSize.SMALL}
                  text={
                    amlUpdateStatus === CustomerScoreUpdateStatus.QUEUED
                      ? 'Updating...'
                      : 'Auto update'
                  }
                  className={cn.updateRiskScoreButton}
                  onClick={() =>
                    amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED &&
                    setPopupVisible(true)
                  }
                />
              )}
            </div>
          )}
          {changeLogsAvailable && (
            <div className={cn.riskScoreMenuWrapper}>
              <ClickOutsideDetector
                setOptionsListVisible={setRiskScoreMenuVisible}
              >
                <>
                  <button
                    type="button"
                    className={`${cn.riskScoreMenuButton} ${
                      riskScoreMenuVisible ? cn.active : ''
                    }`}
                    onClick={() =>
                      setRiskScoreMenuVisible(!riskScoreMenuVisible)
                    }
                  >
                    <IconDots />
                  </button>
                  {riskScoreMenuVisible && (
                    <div className={cn.riskScoreMenu}>
                      <button
                        type="button"
                        className={cn.riskScoreMenuListButton}
                        onClick={() => {
                          setRiskScoreChangeLogsSidepanelVisible(true);
                          setRiskScoreMenuVisible(false);
                        }}
                      >
                        Change Log
                      </button>
                    </div>
                  )}
                  <div />
                </>
              </ClickOutsideDetector>
            </div>
          )}
        </div>
      </div>
      {expanded && logs && (
        <div className={`${cn.totalRiskLogs} ${totalRiskClassName}`}>
          {logs &&
            logs.map((item) => (
              <div className={cn.totalRiskLogsItem} key={item.id}>
                <div className={cn.totalRiskLogsItemLeft}>
                  {item.event}
                  {item.comment && (
                    <div className={cn.totalRiskLogsItemLeftComment}>
                      {item.comment}
                    </div>
                  )}
                </div>
                <div className={cn.totalRiskLogsItemRight}>
                  <div className={cn.totalRiskLogsItemRightUser}>
                    {item.user}
                  </div>
                  <div className={cn.totalRiskLogsItemRightDate}>
                    {formatDate(item.date, true)}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
      <Popup
        title={
          aml?.clientScoreType === ClientScoreTypeEnum.MANUAL
            ? 'Customer Risk Score was set manually. The update will return Customer Risk Score to the automatically calculated values'
            : 'Customer Risk Score will updated'
        }
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => onUpdate && onUpdate()}
        cancelButtonText="Cancel"
        okButtonText={updateInProgress ? 'Loading...' : 'Update'}
        isVisible={popupVisible}
      />
    </div>
  );
};

export default TotalRiskScoring;
